import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-2 whitespace-nowrap font-satoshi font-bold tracking-tight",
    "transition-all duration-150 ease-out",
    "disabled:pointer-events-none disabled:opacity-50",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember/40 focus-visible:ring-offset-2",
    "active:scale-[0.97]",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: "bg-ink text-paper hover:bg-lime hover:text-ink",
        secondary: "bg-lime text-ink hover:bg-lime-dark hover:text-paper",
        urgent: "bg-ember text-paper hover:bg-ember-dark",
        accent: "bg-sun text-ink hover:bg-sun-dark hover:text-paper",
        ghost: "bg-transparent text-ink border-2 border-smoke hover:border-ink",
        danger: "bg-danger text-paper hover:opacity-90",
        link: "bg-transparent text-ink underline-offset-4 hover:underline p-0 h-auto",
      },
      size: {
        sm: "h-9 px-4 text-sm rounded-full",
        md: "h-11 px-5 text-sm rounded-full",
        lg: "h-12 px-6 text-base rounded-full",
        xl: "h-14 px-8 text-base rounded-full",
        icon: "h-11 w-11 rounded-full",
        iconSm: "h-9 w-9 rounded-full",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "md",
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };

const spinnerSizeFor = (size: ButtonProps["size"]) => {
  switch (size) {
    case "lg":
    case "xl":
    case "icon":
      return "md" as const;
    case "sm":
    case "iconSm":
      return "sm" as const;
    default:
      return "sm" as const;
  }
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, fullWidth, className }))}
      {...props}
    >
      {loading ? (
        <>
          <span className="invisible inline-flex items-center gap-2">
            {children}
          </span>
          <span className="absolute inset-0 inline-flex items-center justify-center">
            <Spinner size={spinnerSizeFor(size)} />
          </span>
        </>
      ) : (
        children
      )}
    </button>
  ),
);
Button.displayName = "Button";

export { buttonVariants };
